import Cookies from "js-cookie";
import store from "@utils/redux/store";
import { apiCreateVisit, apiUpdateVisit } from "./VisitApi";

const REFERRER_MATCH = [
  "^http://localhost:8080",
  "^https://.*.noom.com",
  "^https://buyflow-lambda.(dev|test).wsli.dev",
  "^https://buyflow-web-assets.test.wsli.dev",
]
  .map(domain => `(${domain})`)
  .reduce((val, acc) => `${acc}|${val}`);

const REFERRER_TO_UTM_SOURCE_MAPPING = {
  none: "$direct",
  ".*google\\..*": "google_organic",
  ".*facebook\\..*": "facebook_organic",
  ".*instagram\\..*": "instagram_organic",
  ".*pinterest\\..*": "pinterest_organic",
  ".*bing\\..*": "bing_organic",
  ".*twitter\\..*": "twitter_organic",
  ".*noom\\..*": "noom_internal",
};

const URL_PARAMS_SESSION_KEY = "urlParams";
const EXTERNAL_URL_PARAM_COOKIE_KEY = "external_url_params";

const OPERATION_ENUM = {
  CREATE: "CREATE",
  UPDATE: "UPDATE",
  NOOP: "NOOP",
};

export default class VisitTracker {
  constructor(sessionCookieDomain) {
    this.operation = OPERATION_ENUM.CREATE;
    this.urlParams = VisitTracker.retrieveStoredUrlParams();
    const urlParamsSet = !!this.urlParams;

    let { URL: url } = document;
    let referrer = document.referrer || "none";
    let extractNewUrlParams = true;

    if (urlParamsSet) {
      if (referrer.match(REFERRER_MATCH) !== null) {
        // Case where user goes from LP -> payment -> add ons, etc.
        // Make sure we continue using stored url params.
        this.operation = OPERATION_ENUM.NOOP;
        extractNewUrlParams = false;
      } else {
        // Case where user has entered our buyflow, goes out and then returns again
        // in the same session. Do an update to catch the case where they might be
        // coming back with new url parameters (e.g. clicked different facebook ad).
        // Backend will compare and create a new visit only if necessary.
        this.operation = OPERATION_ENUM.UPDATE;
      }
    } else if (VisitTracker.isUserExternal(referrer)) {
      // Case where visit was created externally and stored in the cookie.
      // Extract visit info from the cookie instead of the document url.
      this.operation = OPERATION_ENUM.NOOP;
      [url, referrer] = VisitTracker.extractFromExternalParams(
        sessionCookieDomain
      );
    } else if (url.match(`app-[0-9]*-day`) !== null) {
      // Case where user is in the in app buyflow. We already created a visit manually
      // in the backend. Just make sure to extract visits from the document url.
      this.operation = OPERATION_ENUM.NOOP;
    }

    // Extract and store new url parameters if needed.
    if (extractNewUrlParams) {
      this.urlParams = VisitTracker.extractUrlParams(url, referrer);
      sessionStorage.setItem(
        URL_PARAMS_SESSION_KEY,
        JSON.stringify(this.urlParams)
      );
    }
  }

  init(rawData = {}) {
    if (this.operation === OPERATION_ENUM.CREATE) {
      VisitTracker.createVisit(this.urlParams, rawData);
    } else if (
      this.operation === OPERATION_ENUM.UPDATE ||
      Object.keys(rawData).length !== 0
    ) {
      VisitTracker.updateVisit(this.urlParams, rawData);
    }
    this.operation = OPERATION_ENUM.NOOP;
  }

  getUrlParams() {
    return this.urlParams;
  }

  updateRawData(rawData) {
    VisitTracker.updateVisit(this.urlParams, rawData);
  }

  static isUserExternal(referrer) {
    return (
      referrer.match("^https://.*.noom.com") !== null &&
      Cookies.get(EXTERNAL_URL_PARAM_COOKIE_KEY)
    );
  }

  static extractFromExternalParams(sessionCookieDomain) {
    const externalUrlParams = JSON.parse(
      decodeURIComponent(Cookies.get(EXTERNAL_URL_PARAM_COOKIE_KEY))
    );
    Cookies.remove(EXTERNAL_URL_PARAM_COOKIE_KEY, {
      path: "/",
      domain: sessionCookieDomain,
    });
    return [externalUrlParams.url, externalUrlParams.referrer];
  }

  static createVisit(urlParams, rawData = {}) {
    const { serverContext } = store.getState();
    const requestBody = {
      id: serverContext.user_id,
      currentUrl: urlParams.initialUrl || document.URL,
      // TODO(sumin): Don't stringify if not necessary (need to update backend).
      urlParams: JSON.stringify(urlParams),
      rawData: {
        country: serverContext.country_code,
        subdivision: serverContext.subdivision,
        language: serverContext.language_code,
        route_id: serverContext.route_id,
        ...rawData,
      },
    };
    apiCreateVisit(requestBody);
  }

  static updateVisit(urlParams, rawData) {
    const { serverContext } = store.getState();
    const requestBody = {
      id: serverContext.user_id,
      currentUrl: urlParams.initialUrl || document.URL,
      // TODO(sumin): Don't stringify if not necessary (need to update backend).
      urlParams: JSON.stringify(urlParams),
      rawData: {
        country: serverContext.country_code,
        subdivision: serverContext.subdivision,
        language: serverContext.language_code,
        route_id: serverContext.route_id,
        ...rawData,
      },
    };
    apiUpdateVisit(requestBody);
  }

  static extractUrlParams(rawUrl, referrer) {
    const url = new URL(rawUrl);
    const searchParams = new URLSearchParams(url.search);
    const formattedUrlParams = {};
    searchParams.forEach((value, key) => {
      formattedUrlParams[key] = value;
    });
    formattedUrlParams.utm_source = VisitTracker.extractUtmSource(
      searchParams,
      referrer
    );
    formattedUrlParams.referrer = referrer;
    formattedUrlParams.initialUrl = url;
    return formattedUrlParams;
  }

  static retrieveStoredUrlParams() {
    return JSON.parse(sessionStorage.getItem(URL_PARAMS_SESSION_KEY));
  }

  static extractUtmSource(searchParams, referrer) {
    const utmSourceFromURL = searchParams.get("utm_source");
    const utmSourceFromReferrer = VisitTracker.getUtmSourceFromReferrer(
      referrer
    );
    return utmSourceFromURL || utmSourceFromReferrer || "$direct";
  }

  static getUtmSourceFromReferrer(referrer) {
    if (!referrer) {
      return undefined;
    }

    return (
      REFERRER_TO_UTM_SOURCE_MAPPING[
        Object.keys(REFERRER_TO_UTM_SOURCE_MAPPING).find(key =>
          referrer.match(key)
        )
      ] || "other"
    );
  }
}
